import { useCallback, useEffect, useMemo } from 'react';
import { v1 as uuid } from 'uuid';

import {
  Box,
  Button,
  ButtonVariants,
  CustomerLedgerUpdateTypeEnum,
  Divider,
  FormControlSizes,
  FormGroup,
  HStack,
  InlineFormattedNumber,
  Input,
  NumberInput,
  SearchSelect,
  Text,
  ToggleGroup,
  TransactionAutoProposalModeEnum,
  useMarketAccountsContext,
  VStack,
  type ToggleGroupOption,
} from '@talos/kyoko';

import { AssetAliasSelector } from '@src/containers/Dealer/CustomerBalances/AssetAliasSelector';
import { useTransferOptions } from '@src/containers/Dealer/CustomerBalances/hooks';
import type { CustomerBalanceTransactionDrawerState } from '@src/containers/Dealer/CustomerBalances/types';
import { useOnlineTransferMarkets } from '@src/containers/Transfers';
import {
  getAccountAvailableBalance,
  type BalancesByCurrencyMarketAccountID,
} from '@src/containers/Transfers/transferOptionUtils';
import {
  errorWithHelp,
  getTransferAccountsCompatibilityError,
  shouldShowDestinationBalance,
} from '@src/containers/Transfers/types';
import { useFeatureFlag } from '@src/hooks';
import { noop } from 'lodash-es';

interface SectionHeaderProps {
  title: string;
  badge?: string;
}

function SectionHeader({ title, badge }: SectionHeaderProps) {
  return (
    <>
      <HStack justifyContent="flex-start" gap="spacing8">
        <Text color="colorTextImportant" fontWeight="fontWeightSemiBold" fontSize="fontSizeMedium">
          {title}
        </Text>
        {badge && (
          <Box
            px="spacing4"
            py="0"
            background="colors.gray.040"
            borderRadius="borderRadiusSmall"
            justifyContent="center"
            alignItems="center"
          >
            <Text color="colorTextSubtle" fontSize="fontSizeSmall">
              {badge}
            </Text>
          </Box>
        )}
      </HStack>
      <Divider mx="-spacing16" color="colors.gray.000" />
    </>
  );
}

const TRANSFER_METHOD_OPTIONS: ToggleGroupOption<'initiate' | 'manual'>[] = [
  { value: 'initiate', displayName: 'Initiate Transfer' },
  { value: 'manual', displayName: 'Book Manual Transfer' },
];

interface TransferSourceDestinationFormProps {
  drawerState: CustomerBalanceTransactionDrawerState;
  updateDrawerState: (updates: Partial<CustomerBalanceTransactionDrawerState>) => void;
  showOnlyConfirmTransferFlow?: boolean;
  balancesByCurrencyMarketAccountID: BalancesByCurrencyMarketAccountID | undefined;
}

export function TransferSourceDestinationForm({
  drawerState,
  updateDrawerState,
  showOnlyConfirmTransferFlow = false,
  balancesByCurrencyMarketAccountID,
}: TransferSourceDestinationFormProps) {
  const { customerTransactionAutoProposalMode } = useFeatureFlag();
  const { marketAccountsByName } = useMarketAccountsContext();
  const providerOptions = useOnlineTransferMarkets();
  const { sourceOptions, destinationOptions } = useTransferOptions({
    drawerState,
    balancesByCurrencyMarketAccountID,
  });
  const destinationError = getTransferAccountsCompatibilityError({
    source: drawerState.source,
    destination: drawerState.destination,
    marketAccountsByName,
  });

  const sourceAvailableBalance = useMemo(() => {
    if (!drawerState.source || !drawerState.aliasCurrency) {
      return undefined;
    }

    const marketAccountId = sourceOptions.find(s => s.marketAccount.Name === drawerState.source)?.marketAccount
      .MarketAccountID;
    if (marketAccountId == null) {
      return undefined;
    }

    return getAccountAvailableBalance(marketAccountId, drawerState.aliasCurrency, balancesByCurrencyMarketAccountID);
  }, [balancesByCurrencyMarketAccountID, drawerState.aliasCurrency, drawerState.source, sourceOptions]);

  const destinationAvailableBalance = useMemo(() => {
    if (!drawerState.destination || !drawerState.aliasCurrency) {
      return undefined;
    }

    const destinationOption = destinationOptions.find(d => d.marketAccount.Name === drawerState.destination);
    if (destinationOption == null || !shouldShowDestinationBalance(destinationOption.marketAccount)) {
      return undefined;
    }

    return getAccountAvailableBalance(
      destinationOption.marketAccount.MarketAccountID,
      drawerState.aliasCurrency,
      balancesByCurrencyMarketAccountID
    );
  }, [balancesByCurrencyMarketAccountID, drawerState.aliasCurrency, drawerState.destination, destinationOptions]);

  // Create filtered transfer method options based on auto proposal mode
  const transferMethodOptions = useMemo(() => {
    if (
      // Only show initiate transfer when auto proposal mode is None and transfer type is withdraw
      customerTransactionAutoProposalMode === TransactionAutoProposalModeEnum.None &&
      drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw
    ) {
      return TRANSFER_METHOD_OPTIONS.filter(option => option.value === 'initiate');
    }
    if (
      // Only show manual transfer when auto proposal mode is None or transfer type is deposit
      customerTransactionAutoProposalMode === TransactionAutoProposalModeEnum.None ||
      drawerState.transferType === CustomerLedgerUpdateTypeEnum.Deposit
    ) {
      return TRANSFER_METHOD_OPTIONS.filter(option => option.value === 'manual');
    }
    return TRANSFER_METHOD_OPTIONS;
  }, [customerTransactionAutoProposalMode, drawerState.transferType]);

  // Update transfer method to 'manual' when only manual option is available
  useEffect(() => {
    const onlyManualAvailable = transferMethodOptions.length === 1 && transferMethodOptions[0].value === 'manual';
    if (onlyManualAvailable && drawerState.transferMethod !== 'manual') {
      updateDrawerState({ transferMethod: 'manual' });
    }
  }, [transferMethodOptions, drawerState.transferMethod, updateDrawerState]);

  const handleGenerateExternalID = useCallback(() => {
    updateDrawerState({ externalID: uuid() });
  }, [updateDrawerState]);

  const getBadgeText = (transferType?: CustomerLedgerUpdateTypeEnum) => {
    switch (transferType) {
      case CustomerLedgerUpdateTypeEnum.Deposit:
        return 'Deposit';
      case CustomerLedgerUpdateTypeEnum.Withdraw:
        return 'Withdraw';
      default:
        return transferType;
    }
  };

  return (
    <Box background="colors.gray.030" p="spacing16" borderRadius="borderRadiusMedium" w="100%">
      <VStack gap="spacing16" w="100%" alignItems="stretch">
        <SectionHeader
          title={drawerState.transferMethod === 'initiate' ? 'Transfer' : 'Book Manual Transfer'}
          badge={getBadgeText(drawerState.transferType)}
        />

        {/* Transfer Method Toggle, only shown if multiple options are available */}
        {transferMethodOptions.length > 1 && (
          <FormGroup mb={0} label="Transfer Method">
            <ToggleGroup
              value={drawerState.transferMethod}
              defaultSelectedVariant={ButtonVariants.Priority}
              options={transferMethodOptions}
              onChange={transferMethod => updateDrawerState({ transferMethod })}
              data-testid="new-transfer-form-method-selector"
              boxProps={{ w: '100%' }}
              size={FormControlSizes.Default}
            />
          </FormGroup>
        )}

        {/* Initiate Transfer specific fields */}
        {drawerState.transferMethod === 'initiate' &&
          drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && (
            <>
              {/* Provider */}
              <FormGroup mb={0} label="Provider">
                <SearchSelect
                  selection={providerOptions.find(p => p.Name === drawerState.provider)}
                  options={providerOptions}
                  onChange={selectedProvider =>
                    updateDrawerState({
                      provider: selectedProvider?.Name || '',
                      source: undefined,
                      destination: undefined,
                    })
                  }
                  getLabel={selectedProvider => selectedProvider.DisplayName}
                  data-testid="new-transfer-form-provider-select"
                  placeholder="Select Provider"
                  showClear
                />
              </FormGroup>

              {/* Asset and read-only amount */}
              <HStack gap="spacing16" w="100%" alignItems="flex-start">
                <FormGroup mb={0} label="Asset" flex={1}>
                  <AssetAliasSelector
                    selectedCurrency={drawerState.currency}
                    selectedAlias={drawerState.aliasCurrency}
                    onChange={alias => updateDrawerState({ aliasCurrency: alias })}
                    disabled={!drawerState.currency}
                  />
                </FormGroup>
                <FormGroup mb={0} label="Amount" flex={1}>
                  <NumberInput
                    value={drawerState.amount ?? ''}
                    suffix={drawerState.aliasCurrency}
                    onChange={noop} // NumberInput requires onChange even when read-only.
                    readOnly={true}
                    data-testid="new-transfer-form-amount-input-disabled"
                  />
                </FormGroup>
              </HStack>

              {/* Source */}
              <FormGroup
                mb={0}
                withMessageSpacing
                label="Source"
                help={
                  sourceAvailableBalance ? (
                    <>
                      Available Balance:{' '}
                      <InlineFormattedNumber
                        number={sourceAvailableBalance.availableAmount}
                        currency={sourceAvailableBalance.currency}
                        truncate
                        highlightColor="inherit"
                      />
                    </>
                  ) : undefined
                }
              >
                <SearchSelect
                  selection={sourceOptions.find(s => s.marketAccount.Name === drawerState.source)}
                  options={sourceOptions}
                  onChange={selectedSource => updateDrawerState({ source: selectedSource?.marketAccount.Name })}
                  getLabel={selectedSource => selectedSource?.label || ''}
                  getDescription={selectedSource => selectedSource?.description ?? ''}
                  data-testid="new-transfer-form-source-select"
                  placeholder="Select Source"
                  disabled={!drawerState.provider}
                  showClear
                />
              </FormGroup>

              {/* Destination */}
              <FormGroup
                mb={0}
                withMessageSpacing
                label="Destination"
                {...errorWithHelp(
                  destinationError,
                  destinationAvailableBalance ? (
                    <>
                      Available Balance:{' '}
                      <InlineFormattedNumber
                        number={destinationAvailableBalance.availableAmount}
                        currency={destinationAvailableBalance.currency}
                        truncate
                        highlightColor="inherit"
                      />
                    </>
                  ) : undefined
                )}
              >
                <SearchSelect
                  selection={destinationOptions.find(d => d.marketAccount.Name === drawerState.destination)}
                  options={destinationOptions}
                  onChange={selectedDestination =>
                    updateDrawerState({ destination: selectedDestination?.marketAccount.Name || '' })
                  }
                  getLabel={selectedDestination => selectedDestination?.label || ''}
                  getDescription={selectedDestination => selectedDestination?.description ?? ''}
                  data-testid="new-transfer-form-destination-select"
                  placeholder="Select Destination"
                  disabled={!drawerState.provider}
                  showClear
                />
              </FormGroup>
            </>
          )}

        {/* Manual Transfer specific fields */}
        {drawerState.transferMethod === 'manual' && (
          <FormGroup
            mb={0}
            label="TX Hash"
            tooltip="Optional transaction hash for manually created transactions. Recommended for record keeping and reconciliation."
          >
            <Input
              value={drawerState.txHash}
              autoComplete="off"
              onChange={e => updateDrawerState({ txHash: e.target.value })}
              data-testid="new-transfer-form-tx-hash-input"
              placeholder="Optional"
            />
          </FormGroup>
        )}

        {!(showOnlyConfirmTransferFlow && drawerState.transferMethod === 'manual') && (
          <>
            {/* External ID */}
            <FormGroup mb={0} label="External ID*">
              <Input
                value={drawerState.externalID}
                autoComplete="off"
                onChange={e => updateDrawerState({ externalID: e.target.value })}
                data-testid="new-transfer-form-external-id-input"
                placeholder="Required"
                maxLength={36}
                suffix={
                  <Button
                    size={FormControlSizes.Small}
                    variant={ButtonVariants.Default}
                    onClick={handleGenerateExternalID}
                    ghost
                  >
                    Generate
                  </Button>
                }
              />
            </FormGroup>

            <Divider my="0" />

            {/* Internal Comments */}
            <FormGroup mb={0} label="Internal Comment">
              <Input
                value={drawerState.internalComments}
                autoComplete="off"
                onChange={e => updateDrawerState({ internalComments: e.target.value })}
                placeholder="Optional"
                data-testid="new-transfer-form-internal-comments"
              />
            </FormGroup>

            {/* External Comments */}
            <FormGroup mb={0} label="External Comment">
              <Input
                value={drawerState.externalComments}
                autoComplete="off"
                onChange={e => updateDrawerState({ externalComments: e.target.value })}
                placeholder="Optional"
                data-testid="new-transfer-form-external-comments"
              />
            </FormGroup>
          </>
        )}
      </VStack>
    </Box>
  );
}
